// AcceptInvite.jsx — the missing /invite/:token landing page.
//
// Before this page existed, invite links (civixcrm.ai/invite/<token>) 404'd
// and invitees fell back to /login → "Create account", which created ghost
// tenants instead of joining the inviting tenant.
//
// Flow:
//   1. GET /api/team/invites/:token — read invite metadata (email/tenant/role)
//   2. Show a friendly landing screen with the invitee's name + password prompt
//   3. POST /api/team/invites/:token/accept — creates the Agent in the correct
//      tenant, returns a session JWT
//   4. Store session, redirect to /
//      • If requireAdminApproval=true, the account is inactive until the admin
//        clicks Approve on /team — the user sees a wait screen from the shell.
//
// Error surfaces:
//   • invite not found / expired / already accepted → clear message + link to
//     login for existing accounts
//   • password too weak → inline error
//   • email mismatch → the accept endpoint enforces this server-side; here we
//     just show the invitee email read-only so it can't drift.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiGet, apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import Logo from '../components/Logo.jsx';
import AIOrb from '../components/AIOrb.jsx';

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setSession } = useAuth();

  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await apiGet(`/team/invites/${token}`);
        if (cancelled) return;
        if (!r?.ok) {
          setStatus('error');
          setErrorMsg(r?.error || 'Invite could not be loaded.');
          return;
        }
        setInvite(r.invite);
        setName(r.invite.name || '');
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(err?.response?.data?.error || err?.message || 'Invite not found or expired.');
      }
    }
    load();
    return () => { cancelled = true; };
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiPost(`/team/invites/${token}/accept`, {
        name: name.trim() || undefined,
        password,
        phone: phone.trim() || undefined,
      });
      if (!r?.ok) throw new Error(r?.error || 'Accept failed');
      if (r.token && r.agent) {
        setSession(r.token, r.agent);
        toast.success('Account created — welcome to CIVIX');
        // Small delay so zustand state propagates before nav.
        setTimeout(() => nav('/phone-setup', { replace: true }), 50);
      } else {
        // No token returned (shouldn't happen post-fix). Send them to login.
        toast.success('Account created — please sign in.');
        setTimeout(() => nav('/login', { replace: true }), 50);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not accept invite';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-white/60 text-sm">Loading invite…</div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="panel max-w-md w-full p-8 text-center">
          <div className="mb-6 flex justify-center"><Logo size="md" /></div>
          <h1 className="text-2xl font-display font-bold mb-2">Invite unavailable</h1>
          <p className="text-white/60 text-sm mb-6">{errorMsg}</p>
          <Link to="/login" className="btn-primary inline-block">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="panel w-full max-w-5xl grid md:grid-cols-2 overflow-hidden relative">
        {/* Left — brand */}
        <div className="hidden md:flex flex-col justify-between p-10 bg-linear-to-br from-civix-purpleDeep/30 to-transparent border-r border-civix-border">
          <Logo size="lg" />
          <div className="flex flex-col items-center gap-6">
            <AIOrb size={220} />
            <div className="text-center">
              <div className="font-display text-2xl font-semibold gradient-text mb-1">
                Welcome to {invite.tenantName}
              </div>
              <div className="text-sm text-white/60 max-w-xs">
                You've been invited to join as {invite.role === 'admin' ? 'an admin' : 'an agent'}. Finish creating your account below.
              </div>
            </div>
          </div>
          <div className="text-xs text-white/40 space-y-1">
            <div>✦ AI-powered lead routing & screening</div>
            <div>✦ Two-way SMS, voice, drip campaigns</div>
            <div>✦ Built-in compliance, recycling, retargeting</div>
          </div>
        </div>

        {/* Right — form */}
        <div className="p-8 md:p-10">
          <div className="md:hidden mb-8"><Logo size="md" /></div>
          <h1 className="text-3xl font-display font-bold mb-1">Accept your invite</h1>
          <p className="text-white/50 mb-8">
            Joining <span className="text-white/80">{invite.tenantName}</span> as <span className="text-white/80">{invite.role}</span>.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label">Email</label>
              <input
                className="input mt-1 opacity-70 cursor-not-allowed"
                value={invite.email}
                readOnly
              />
              <div className="text-[11px] text-white/40 mt-1">
                This invite is bound to this email address.
              </div>
            </div>
            <div>
              <label className="label">Full Name</label>
              <input
                className="input mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
              />
            </div>
            <div>
              <label className="label">Phone <span className="text-white/40 font-normal">(optional)</span></label>
              <input
                className="input mt-1"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 555-1234"
              />
            </div>
            <div>
              <label className="label">Password</label>
              <input
                className="input mt-1"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
              <div className="text-[11px] text-white/40 mt-1">
                At least 8 characters.
              </div>
            </div>
            {invite.requiresApproval && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                An admin will need to approve your account before you can log in.
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full mt-2 disabled:opacity-60"
            >
              {submitting ? 'Creating account…' : 'Accept invite & continue'}
            </button>
            <div className="text-center text-xs text-white/40">
              Already have an account? <Link to="/login" className="text-civix-purpleSoft hover:text-civix-cyan">Sign in</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
